import { PencilIcon } from '@heroicons/react/24/solid';
import { Select, SelectItem } from '@heroui/select';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import type { SiteDto } from '@/api/generated/schemas';
import { YES_NO_OPTIONS } from '@/constants';

import { EMPTY_VALUE, EMPTY_VALUE_CLASS } from './emptyValue';
import { useEditableField } from './useEditableField';

export interface EditableSelectOption {
  label: string;
  value: string;
}

export interface EditableSelectFieldProps {
  label: string;
  fieldPath: string;
  siteId: string;
  site: SiteDto;
  /**
   * Options shown in the dropdown. Defaults to Yes/No.
   * Both `label` (display) and `value` (stored value) are required so the field
   * can render strings that don't match the stored value verbatim if needed.
   */
  options?: ReadonlyArray<EditableSelectOption>;
  disabled?: boolean;
  isRequired?: boolean;
}

const DEFAULT_OPTIONS: ReadonlyArray<EditableSelectOption> = YES_NO_OPTIONS.map((o) => ({
  label: o.label,
  value: o.label,
}));

export const EditableSelectField: FC<EditableSelectFieldProps> = ({
  label,
  fieldPath,
  siteId,
  site,
  options = DEFAULT_OPTIONS,
  disabled = false,
  isRequired = false,
}) => {
  const { currentValue, isEditing, editValue, error, isSaving, setEditValue, enterEditMode, handleSave, handleCancel } =
    useEditableField<string>({
      siteId,
      site,
      fieldPath,
      label,
    });

  const displayValue =
    currentValue == null || currentValue === ''
      ? EMPTY_VALUE
      : (options.find((o) => o.value === currentValue)?.label ?? String(currentValue));

  // Handle escape key to cancel editing
  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      } else if (e.key === 'Enter') {
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, handleCancel, handleSave]);

  // Handle click outside to save
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        handleSave();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, handleSave]);

  if (!isEditing) {
    // View mode
    return (
      <div>
        <p className="text-sm text-default-500 mb-0.5">
          {label}
          {isRequired && <span className="text-danger ml-1">*</span>}
        </p>
        <div
          className={`font-medium group cursor-pointer hover:bg-default-100 rounded px-2 py-0.5 -mx-2 transition-colors ${
            disabled ? 'cursor-not-allowed opacity-50' : ''
          }`}
          onClick={() => !disabled && enterEditMode()}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              enterEditMode();
            }
          }}
        >
          <div className="flex items-start gap-2 min-w-0">
            <span
              className={`truncate flex-1 ${displayValue === EMPTY_VALUE ? EMPTY_VALUE_CLASS : ''}`}
              title={displayValue}
            >
              {displayValue}
            </span>
            {!disabled && (
              <PencilIcon className="w-3 h-3 text-default-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div ref={wrapperRef}>
      <Select
        label={label}
        selectedKeys={editValue ? [editValue] : []}
        onSelectionChange={(keys) => {
          const selected = Array.from(keys)[0] as string;
          setEditValue(selected);
        }}
        isDisabled={isSaving}
        isInvalid={!!error}
        errorMessage={error}
        size="sm"
        isRequired={isRequired}
      >
        {options.map((option) => (
          <SelectItem key={option.value}>{option.label}</SelectItem>
        ))}
      </Select>
    </div>
  );
};
